/**
 * MONSTER TOWN — RESTAURANT ART. OWNED BY THE VENUE CREW (src/venue/**).
 *
 * WHY THIS FILE EXISTS.
 *
 * Venue #2 was a data drop in the sense that mattered to the engine — one
 * `defineVenue`, seven placeables, no code in `src/venue/**` that knows what a
 * restaurant is — and that part still holds. But it reused the ice cream shop's
 * FIXTURE ART unchanged, so the two rooms shipped with identical round bistro
 * tables, identical mushroom stools, an identical counter, an identical teal
 * bin and the same checkerboard floor in a different colour. Mechanically a
 * second venue; visually a palette swap. A kid would not have believed they had
 * walked in somewhere new, which is the only test that counts.
 *
 * So: a restaurant has a KITCHEN (cast-iron ranges with pots that steam, a
 * plate dresser, a pass with a heat lamp) and a DINING ROOM (long clothed
 * tables, chairs with backs, a scraps pail). None of it is ice cream, and none
 * of it is engine — this is still content, it is just content with its own
 * silhouettes instead of borrowed ones.
 *
 * Same contract as every other builder: origin at the footprint centre, floor
 * at y = 0, no baked position or rotation, deterministic, kit materials only,
 * ink outlines on silhouettes only.
 */

import * as THREE from 'three'
import type { ArtContext } from '../core/placeable'
import type { PaletteName } from '../core/palette'
import { ball, boxOn, cone, cylinder, group, inked } from '../render/kit'
import type { VenueItem } from './types'

/**
 * SEAT GEOMETRY IS A CROSS-FILE FACT, STATED ONCE PER FILE AND KEPT IDENTICAL.
 *
 * `customers.ts` seats a monster on a wide seat placeable at `TABLE_SEAT_X` with
 * its backside at `TABLE_SEAT_Y`, and on a 1x1 seat at `STOOL_SEAT_Y`. Those are
 * the numbers below. If a chair's seat is anywhere else, a customer sits in
 * mid-air or gets run through by the furniture — which is exactly what happened
 * the last time these drifted apart.
 */
const CHAIR_X = 0.95
const TABLE_SEAT_Y = 0.48
const STOOL_SEAT_Y = 0.56

/**
 * The back of a chair. The single clearest "this is a restaurant" silhouette.
 *
 * DRAW-CALL DISCIPLINE: two posts and two rails, and that is the whole part.
 * Three rails read no differently at this size and the restaurant seats up to
 * five chairs plus a standalone, which is where the cost multiplies.
 */
function chairBack(t: number, seatY: number, facing: number): THREE.Group {
  const g = group('chairBack')
  g.rotation.y = facing
  const post = (x: number): void => {
    g.add(cylinder(t * 0.045, t * (seatY + 0.52), { color: 'woodDark', x: x * t, z: -t * 0.2, seg: 7 }))
  }
  post(-0.19)
  post(0.19)
  for (let i = 0; i < 2; i++) {
    g.add(
      boxOn(t * 0.44, t * 0.085, t * 0.06, {
        color: 'woodDark',
        y: t * (seatY + 0.24 + i * 0.18),
        z: -t * 0.2,
      }),
    )
  }
  return g
}

/**
 * A DINING CHAIR — cushioned seat, four legs, and a tall slatted back.
 *
 * Deliberately the opposite silhouette to the ice cream shop's mushroom stool:
 * square instead of round, tall instead of squat, wood instead of candy colours.
 * At iso, the back is the part that reads from across the room.
 */
export function diningChairArt(ctx: ArtContext): THREE.Object3D {
  const t = ctx.grid.tileSize
  const g = group('diningChair')
  const seat = boxOn(t * 0.52, t * 0.1, t * 0.52, {
    color: 'fabricAlt',
    y: t * (STOOL_SEAT_Y - 0.1),
    name: 'seat',
  })
  g.add(inked(seat))
  for (const [dx, dz] of [
    [-0.19, -0.19],
    [0.19, -0.19],
    [-0.19, 0.19],
    [0.19, 0.19],
  ] as [number, number][]) {
    g.add(cylinder(t * 0.045, t * (STOOL_SEAT_Y - 0.1), { color: 'woodDark', x: dx * t, z: dz * t, seg: 6 }))
  }
  g.add(chairBack(t, STOOL_SEAT_Y, 0))
  return g
}

/**
 * A LAID TABLE FOR TWO — rectangular top under a cloth, with a chair at each
 * end, cutlery, and a candle. The ice cream shop's table is a round pedestal
 * with a flower; this is a dinner table, and they do not read as the same object
 * from any angle.
 */
export function diningTableArt(ctx: ArtContext): THREE.Object3D {
  const t = ctx.grid.tileSize
  const g = group('diningTable')
  const topY = t * 0.66

  // The cloth is the whole trick: a slab that overhangs and drops down the
  // sides turns four legs and a board into "somewhere you eat".
  const top = boxOn(t * 1.06, t * 0.09, t * 0.84, { color: 'woodLight', y: topY - t * 0.09, name: 'top' })
  g.add(inked(top))
  const cloth = boxOn(t * 1.18, t * 0.3, t * 0.96, { color: 'wallCream', y: topY - t * 0.24, name: 'cloth' })
  g.add(inked(cloth))
  // a runner stripe down the middle, so the cloth is not one flat fill
  g.add(boxOn(t * 1.2, t * 0.02, t * 0.26, { color: 'roofRed', y: topY + t * 0.06 }))

  for (const [dx, dz] of [
    [-0.42, -0.3],
    [0.42, -0.3],
    [-0.42, 0.3],
    [0.42, 0.3],
  ] as [number, number][]) {
    g.add(cylinder(t * 0.06, topY - t * 0.1, { color: 'woodDark', x: dx * t, z: dz * t, seg: 7 }))
  }

  // Two places laid, one at each end, and a candle between them. The plate is
  // the part that reads; the cutlery beside it was two more draw calls a
  // three-year-old will never see at this size.
  for (const s of [-1, 1]) {
    g.add(cylinder(t * 0.15, t * 0.03, { color: 'plate', x: s * t * 0.3, y: topY + t * 0.08, seg: 12 }))
  }
  g.add(cylinder(t * 0.045, t * 0.24, { color: 'flowerWhite', y: topY + t * 0.08, seg: 8 }))
  const flame = cone(t * 0.05, t * 0.11, { color: 'uiCoin', y: topY + t * 0.34, seg: 7, name: 'flame' })
  g.add(flame)

  // The chairs. `CHAIR_X` is where `customers.ts` puts a seated monster.
  for (const s of [-1, 1]) {
    const c = group(s < 0 ? 'seatL' : 'seatR')
    c.position.set(s * CHAIR_X * t, 0, 0)
    c.add(boxOn(t * 0.5, t * 0.1, t * 0.5, { color: 'fabric', y: t * (TABLE_SEAT_Y - 0.1) }))
    for (const [dx, dz] of [
      [-0.18, -0.18],
      [0.18, -0.18],
      [-0.18, 0.18],
      [0.18, 0.18],
    ] as [number, number][]) {
      c.add(cylinder(t * 0.04, t * (TABLE_SEAT_Y - 0.1), { color: 'woodDark', x: dx * t, z: dz * t, seg: 6 }))
    }
    // Backs face OUTWARD, away from the table, the way a laid table looks.
    c.add(chairBack(t, TABLE_SEAT_Y, s < 0 ? Math.PI / 2 : -Math.PI / 2))
    g.add(c)
  }
  return g
}

/**
 * A COOKING RANGE with a pot of something on it.
 *
 * The restaurant's stations used to be `tubArt` — an ice cream freezer cabinet,
 * grey metal box with a domed scoop on top. In the captured frame those read as
 * "two grey untextured plinths with coloured blobs balanced on them", one of
 * which the critic took for a monster standing on a plinth. A range has a black
 * top, hob rings, a stubby chimney and a real pot with a lid, so it reads as
 * cooking from across the room.
 *
 * Animated part names match what the placeable's `interact` asks for: `dome` is
 * the food (it bubbles) and `lever` is the ladle (it stirs).
 */
export function stoveArt(ctx: ArtContext, color: PaletteName): THREE.Object3D {
  const t = ctx.grid.tileSize
  const g = group('stove')

  const body = boxOn(t * 0.84, t * 0.56, t * 0.8, { color: 'roofRed', name: 'body' })
  g.add(inked(body))
  // oven door with a window and a handle: the front is where a kid looks
  g.add(boxOn(t * 0.6, t * 0.34, t * 0.06, { color: 'metalDark', y: t * 0.1, z: t * 0.4 }))
  g.add(boxOn(t * 0.4, t * 0.16, t * 0.04, { color: 'sunWarm', y: t * 0.19, z: t * 0.43 }))
  g.add(cylinder(t * 0.03, t * 0.52, { color: 'metal', y: t * 0.47, z: t * 0.44, seg: 7, rotY: 0 }))

  // hot plate
  const plate = boxOn(t * 0.9, t * 0.08, t * 0.86, { color: 'inkSoft', y: t * 0.56, name: 'hob' })
  g.add(plate)
  for (const [hx, hz] of [
    [-0.2, -0.2],
    [0.22, 0.2],
  ] as [number, number][]) {
    const ring = cylinder(t * 0.14, t * 0.03, { color: 'roofRed', x: hx * t, y: t * 0.64, z: hz * t, seg: 12 })
    g.add(ring)
  }

  // the pot: a fat black pan with the food domed above the rim
  const pot = cylinder(t * 0.3, t * 0.26, { color: 'metalDark', y: t * 0.64, seg: 14, name: 'pot' })
  g.add(inked(pot))
  g.add(cylinder(t * 0.33, t * 0.05, { color: 'metal', y: t * 0.88, seg: 14 }))
  const dome = ball(t * 0.27, { color, y: t * 0.94, seg: 12 })
  dome.name = 'dome'
  g.add(dome)
  // two bubbles, so a still frame still says "this is cooking"
  g.add(ball(t * 0.07, { color: 'flowerWhite', x: -t * 0.1, y: t * 1.06, z: t * 0.06, seg: 7 }))
  g.add(ball(t * 0.05, { color: 'flowerWhite', x: t * 0.12, y: t * 1.11, z: -t * 0.04, seg: 7 }))

  // the ladle, hooked over the rim — the part that stirs
  const lever = group('lever')
  lever.position.set(t * 0.26, t * 0.92, 0)
  lever.add(cylinder(t * 0.028, t * 0.36, { color: 'woodLight', seg: 6 }))
  const bowl = ball(t * 0.09, { color: 'metal', y: -t * 0.18, seg: 8 })
  bowl.scale.set(1, 0.6, 1)
  lever.add(bowl)
  lever.rotation.z = -0.3
  g.add(lever)

  // a stubby flue so the silhouette is unmistakably a cooker
  g.add(cylinder(t * 0.07, t * 0.5, { color: 'metalDark', x: -t * 0.32, y: t * 0.6, z: -t * 0.3, seg: 8 }))
  return g
}

/**
 * A PLATE DRESSER — plates standing on edge in a wooden rack, the way a kitchen
 * actually stores them. The ice cream shop's cone stack is a post with three
 * cones balanced on it; this is a piece of furniture.
 *
 * `holder` is the animated part (it lifts when a plate is taken).
 */
export function plateRackArt(ctx: ArtContext): THREE.Object3D {
  const t = ctx.grid.tileSize
  const g = group('plateRack')
  const cab = boxOn(t * 0.8, t * 0.5, t * 0.6, { color: 'wood', name: 'cabinet' })
  g.add(inked(cab))
  g.add(boxOn(t * 0.88, t * 0.07, t * 0.68, { color: 'woodLight', y: t * 0.5 }))
  // two uprights and a rail: the rack
  for (const s of [-1, 1]) {
    g.add(cylinder(t * 0.035, t * 0.5, { color: 'woodDark', x: s * t * 0.32, y: t * 0.57, seg: 6 }))
  }
  g.add(boxOn(t * 0.7, t * 0.05, t * 0.05, { color: 'woodDark', y: t * 1.02 }))

  const holder = group('holder')
  holder.position.y = t * 0.57
  // Plates ON EDGE, slotted in a row. Nothing else in either venue looks
  // remotely like this.
  for (let i = 0; i < 4; i++) {
    const p = cylinder(t * 0.19, t * 0.035, { color: 'plate', x: (i - 1.5) * t * 0.16, y: t * 0.2, seg: 14 })
    p.rotation.x = Math.PI / 2
    p.rotation.z = 0.12
    holder.add(p)
  }
  g.add(holder)
  return g
}

/**
 * THE PASS — where a kitchen hands food to the room. A counter with an overhead
 * heat-lamp bar, an order spike and a service bell.
 *
 * `bell` is the animated part, same as the ice cream counter, so the "ring for
 * the next customer" interaction is identical to play even though the object is
 * completely different to look at.
 */
export function passCounterArt(ctx: ArtContext): THREE.Object3D {
  const t = ctx.grid.tileSize
  const g = group('passCounter')

  const body = boxOn(t * 1.9, t * 0.74, t * 0.8, { color: 'wallCream', name: 'body' })
  g.add(inked(body))
  // stainless top, tiled front — a working kitchen surface, not a shop counter
  g.add(boxOn(t * 2.0, t * 0.1, t * 0.9, { color: 'metal', y: t * 0.74, name: 'top' }))
  for (let i = 0; i < 3; i++) {
    g.add(
      boxOn(t * 0.44, t * 0.34, t * 0.04, {
        color: 'wallBlue',
        x: (i - 1) * t * 0.56,
        y: t * 0.22,
        z: t * 0.42,
      }),
    )
  }

  // the heat lamp: two posts and a glowing bar over the surface
  for (const s of [-1, 1]) {
    g.add(cylinder(t * 0.04, t * 0.66, { color: 'metalDark', x: s * t * 0.8, y: t * 0.84, z: -t * 0.2, seg: 7 }))
  }
  const hood = boxOn(t * 1.7, t * 0.14, t * 0.24, { color: 'metalDark', y: t * 1.5, z: -t * 0.2, name: 'hood' })
  g.add(inked(hood))
  g.add(boxOn(t * 1.5, t * 0.07, t * 0.16, { color: 'sunWarm', y: t * 1.43, z: -t * 0.2, name: 'lamp' }))

  // an order spike with a ticket on it
  g.add(cylinder(t * 0.015, t * 0.3, { color: 'metal', x: t * 0.62, y: t * 0.84, seg: 6 }))
  g.add(
    boxOn(t * 0.2, t * 0.24, t * 0.02, {
      color: 'flowerWhite',
      x: t * 0.62,
      y: t * 0.9,
      z: t * 0.03,
      rotY: 0.2,
    }),
  )

  const bell = ball(t * 0.13, { color: 'uiCoin', x: -t * 0.66, y: t * 0.92, seg: 10 })
  bell.name = 'bell'
  g.add(bell)
  g.add(cylinder(t * 0.16, t * 0.04, { color: 'uiCoinDark', x: -t * 0.66, y: t * 0.84, seg: 10 }))
  return g
}

/**
 * A SCRAPS PAIL — the restaurant's start-over bin. A wooden pail with a rope
 * handle, next to the ice cream shop's round teal pedal bin. Same job, same
 * friendly poof, different object.
 */
export function scrapPailArt(ctx: ArtContext): THREE.Object3D {
  const t = ctx.grid.tileSize
  const g = group('scrapPail')
  const body = cylinder(t * 0.32, t * 0.54, { color: 'wood', seg: 12, name: 'body' })
  g.add(inked(body))
  for (const y of [0.12, 0.44]) {
    g.add(cylinder(t * 0.34, t * 0.05, { color: 'metalDark', y: t * y, seg: 12 }))
  }
  const lid = cylinder(t * 0.34, t * 0.08, { color: 'woodDark', y: t * 0.54, seg: 12, name: 'lid' })
  g.add(lid)
  // rope handle, drawn as three beads so it reads at 40 px
  for (let i = 0; i < 3; i++) {
    g.add(ball(t * 0.05, { color: 'woodLight', x: (i - 1) * t * 0.16, y: t * (0.68 - Math.abs(i - 1) * 0.06), seg: 7 }))
  }
  return g
}

/**
 * A CHALKBOARD SPECIALS BOARD — the restaurant's picture menu. Pictures of what
 * the kitchen can cook, no words (LAW #4). A framed slate on an A-frame, not the
 * ice cream shop's flat post-mounted sign.
 */
export function specialsBoardArt(ctx: ArtContext, items: readonly VenueItem[]): THREE.Object3D {
  const t = ctx.grid.tileSize
  const g = group('specialsBoard')
  for (const s of [-1, 1]) {
    const leg = cylinder(t * 0.05, t * 0.9, { color: 'woodDark', z: s * t * 0.16, seg: 7 })
    leg.rotation.x = s * 0.14
    g.add(leg)
  }
  const frame = boxOn(t * 0.9, t * 0.86, t * 0.12, { color: 'woodLight', y: t * 0.5, name: 'board' })
  g.add(inked(frame))
  g.add(boxOn(t * 0.74, t * 0.7, t * 0.06, { color: 'inkSoft', y: t * 0.58, z: t * 0.05 }))
  const foods = items.filter((i) => i.kind === 'scoop').slice(0, 4)
  for (let i = 0; i < foods.length; i++) {
    const it = foods[i]
    if (it === undefined) continue
    const col = i % 2
    const row = Math.floor(i / 2)
    // a plate with a portion on it, so the board says "dinner" not "flavours"
    const p = cylinder(t * 0.1, t * 0.02, {
      color: 'plate',
      x: (col - 0.5) * t * 0.36,
      y: t * (1.06 - row * 0.3),
      z: t * 0.09,
      seg: 12,
    })
    p.rotation.x = Math.PI / 2
    g.add(p)
    g.add(
      ball(t * 0.07, {
        color: it.color,
        x: (col - 0.5) * t * 0.36,
        y: t * (1.06 - row * 0.3),
        z: t * 0.12,
        seg: 8,
      }),
    )
  }
  return g
}
